import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

const STORAGE_KEY = 'azurecalc_saved_estimates';

const SERVICE_ICONS: Record<string, string> = {
  'blob-storage': 'folder_open',
  'logic-apps': 'account_tree',
  'functions': 'bolt',
  'app-service': 'dns',
  'bandwidth': 'network_check',
  'firewall': 'shield',
  'log-analytics': 'monitoring',
};

interface SavedItem {
  id: string;
  label: string;
  service: string;
  savedAt: string;
  estimateValue: string;
}

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  } catch {
    return iso;
  }
}

export default function SavedEstimatesPage() {
  const [items, setItems] = useState<SavedItem[]>([]);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      setItems(raw ? JSON.parse(raw) : []);
    } catch {
      setItems([]);
    }
  }, []);

  const removeItem = (id: string) => {
    const updated = items.filter((item) => item.id !== id);
    setItems(updated);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
  };

  return (
    <div className="min-h-screen">
      <div className="max-w-2xl mx-auto px-4 py-8">
        <h1 className="text-2xl font-bold mb-1" style={{ color: 'var(--text-primary)' }}>Saved Estimates</h1>
        <p className="text-[13px] mb-6" style={{ color: 'var(--text-tertiary)' }}>
          Estimates are saved in your browser.{' '}
          <span style={{ color: 'var(--text-tertiary)' }}>Use the share link to access them on another device.</span>
        </p>

        {items.length === 0 ? (
          <div className="rounded-lg p-10 text-center" style={{ backgroundColor: 'var(--surface-container)', border: '1px solid var(--outline-variant)' }}>
            <span className="material-symbols-outlined text-[48px] block mb-3" style={{ color: 'var(--text-tertiary)' }}>bookmark</span>
            <p className="text-[14px] mb-1" style={{ color: 'var(--text-tertiary)' }}>No saved estimates yet.</p>
            <p className="text-[12px] mb-4" style={{ color: 'var(--text-tertiary)' }}>
              Use the <strong style={{ color: 'var(--text-secondary)' }}>Save &amp; Share Estimate</strong> button on any calculator to save an estimate.
            </p>
            <Link to="/" className="text-[#0078d4] text-sm hover:underline">Go to calculators →</Link>
          </div>
        ) : (
          <div className="flex flex-col gap-3">
            {items.map((item) => (
              <div key={item.id} className="rounded-lg px-4 py-3 flex items-center gap-4" style={{ backgroundColor: 'var(--surface-container)', border: '1px solid var(--outline-variant)' }}>
                <div className="w-9 h-9 rounded flex items-center justify-center shrink-0" style={{ backgroundColor: 'var(--surface-container-low)', border: '1px solid rgba(0, 120, 212, 0.2)' }}>
                  <span className="material-symbols-outlined text-[18px] text-[#0078d4]">
                    {SERVICE_ICONS[item.service] ?? 'calculate'}
                  </span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[13px] font-semibold truncate" style={{ color: 'var(--text-secondary)' }}>{item.label}</p>
                  <p className="text-[11px]" style={{ color: 'var(--text-tertiary)' }}>Saved {formatDate(item.savedAt)}</p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Link
                    to={`/estimate/${item.id}`}
                    className="text-[12px] text-[#0078d4] hover:underline px-2 py-1"
                  >
                    View
                  </Link>
                  <button
                    type="button"
                    onClick={() => removeItem(item.id)}
                    className="hover:text-[#a4262c] transition-colors p-1"
                    style={{ color: 'var(--text-tertiary)' }}
                    title="Remove"
                  >
                    <span className="material-symbols-outlined text-[18px]">delete</span>
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
